import { useCallback, useState } from "react";
import { ethers } from "ethers";
import type { TokenInfo } from "../../config";

export type OwnedNftOption = {
  contract: string;
  tokenId: string;
  tokenType: "erc721" | "erc1155";
  name: string;
  symbol: string;
  balance?: string;
};

export function buildCustomNftToken(
  address: string,
  tokenType: "erc721" | "erc1155",
  tokenSubID: string,
  isWrappedNft = false,
): TokenInfo | null {
  const trimmedAddress = address.trim();
  const trimmedSubID = tokenSubID.trim();
  if (!ethers.isAddress(trimmedAddress) || !trimmedSubID) return null;
  const checksumAddress = ethers.getAddress(trimmedAddress);
  const short = `${checksumAddress.slice(0, 6)}...${checksumAddress.slice(-4)}`;
  return {
    address: checksumAddress,
    symbol: tokenType === "erc721" ? "NFT721" : "NFT1155",
    name: `${tokenType.toUpperCase()} ${short}`,
    decimals: 0,
    isNative: false,
    tokenType,
    tokenSubID: trimmedSubID,
    isWrappedNft,
  };
}

interface UseWalletNftDiscoveryParams {
  connected: boolean;
  signer: ethers.Signer | null;
  addLog: (
    message: string,
    type?: "info" | "success" | "error" | "warning",
    txHash?: string,
  ) => void;
}

export function useWalletNftDiscovery({
  connected,
  signer,
  addLog,
}: UseWalletNftDiscoveryParams) {
  const [ownedNftOptions, setOwnedNftOptions] = useState<OwnedNftOption[]>([]);
  const [discoveringNfts, setDiscoveringNfts] = useState(false);
  const [nftDiscoveryError, setNftDiscoveryError] = useState<string | null>(
    null,
  );
  const [selectedOwnedDepositNftIdx, setSelectedOwnedDepositNftIdx] =
    useState<string>("");
  const [selectedOwnedSendNftIdx, setSelectedOwnedSendNftIdx] =
    useState<string>("");

  const discoverWalletNfts = useCallback(async () => {
    if (!connected || !signer) {
      setNftDiscoveryError("Connect wallet first");
      return;
    }
    setDiscoveringNfts(true);
    setNftDiscoveryError(null);
    try {
      const owner = await signer.getAddress();
      const dedup = new Map<string, OwnedNftOption>();

      const addFromAlchemyPayload = (payload: any) => {
        const owned = payload?.ownedNfts || payload?.nfts || [];
        for (const nft of owned) {
          const contract = String(
            nft?.contract?.address ||
              nft?.contractAddress ||
              nft?.contract ||
              "",
          ).trim();
          const tokenIdRaw = String(
            nft?.id?.tokenId ?? nft?.tokenId ?? nft?.identifier ?? "",
          ).trim();
          if (!ethers.isAddress(contract) || !tokenIdRaw) continue;
          const tokenId = BigInt(tokenIdRaw).toString(10);
          const tokenTypeRaw = String(
            nft?.contractMetadata?.tokenType ||
              nft?.tokenType ||
              nft?.token_standard ||
              "ERC721",
          ).toLowerCase();
          const tokenType: "erc721" | "erc1155" = tokenTypeRaw.includes("1155")
            ? "erc1155"
            : "erc721";
          const name = String(
            nft?.title || nft?.name || `${tokenType.toUpperCase()} #${tokenId}`,
          );
          const symbol = String(
            nft?.contractMetadata?.symbol ||
              (tokenType === "erc721" ? "NFT721" : "NFT1155"),
          );
          const balance =
            nft?.balance != null ? String(nft.balance) : undefined;
          const key = `${contract.toLowerCase()}:${tokenId}:${tokenType}`;
          if (!dedup.has(key)) {
            dedup.set(key, {
              contract: ethers.getAddress(contract),
              tokenId,
              tokenType,
              name,
              symbol,
              balance,
            });
          }
        }
      };

      // Alchemy REST NFT API only.
      // Use explicit `path` query to avoid relying on rewrite behavior:
      // /api/proxy/alchemy-nft?path=/getNFTsForOwner&owner=...
      let rpcErrorText = "";
      try {
        let pageKey: string | undefined;
        for (let i = 0; i < 10; i += 1) {
          const query = new URLSearchParams({
            path: "/getNFTsForOwner",
            owner,
            withMetadata: "true",
            pageSize: "100",
          });
          if (pageKey) query.set("pageKey", pageKey);
          const resp = await fetch(
            `/api/proxy/alchemy-nft?${query.toString()}`,
          );
          if (!resp.ok) {
            rpcErrorText = await resp.text();
            break;
          }
          const data = (await resp.json()) as any;
          addFromAlchemyPayload(data);
          pageKey = data?.pageKey;
          if (!pageKey) break;
        }
      } catch {
        // surfaced below
      }

      const discovered = Array.from(dedup.values());
      setOwnedNftOptions(discovered);
      setSelectedOwnedDepositNftIdx("");
      setSelectedOwnedSendNftIdx("");
      if (discovered.length === 0) {
        const hint = rpcErrorText
          ? ` Discovery RPC error: ${rpcErrorText}`
          : "";
        setNftDiscoveryError(
          `No NFTs found for this wallet on Base Sepolia.${hint}`,
        );
        addLog("NFT discovery returned no holdings", "warning");
      } else {
        addLog(`Discovered ${discovered.length} wallet NFT(s)`, "success");
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setNftDiscoveryError(msg);
      addLog(`NFT discovery failed: ${msg}`, "error");
    } finally {
      setDiscoveringNfts(false);
    }
  }, [connected, signer, addLog]);

  return {
    ownedNftOptions,
    discoveringNfts,
    nftDiscoveryError,
    selectedOwnedDepositNftIdx,
    setSelectedOwnedDepositNftIdx,
    selectedOwnedSendNftIdx,
    setSelectedOwnedSendNftIdx,
    discoverWalletNfts,
  };
}
